//src/design-system/Organisms/OrderHistorySection/index.tsx

import { Typography } from "@/design-system/Atoms";
import { orderColumns } from "@/design-system/Organisms/OrderHistorySection/orderTable.data";
import { TableGrid } from "@/design-system/Molecules/TableGrid";
import { DASHBOARD_TABS } from "@/constants/dashboardTabs";
import { useOrdersService } from "@/rtkQuery/features/hooks/useOrdersService";
import { AppEmptyState, AppErrorState } from "@/design-system/Feedbacks";
import OrderTableShimmer from "./OrderTableShimmer";
import { useScrollToTopOnMount } from "@/hooks/useScrollToTopOnMount";
import { Button } from "@/design-system/Atoms";
import { useState } from "react";
import CancelOrderModal from "@/design-system/Molecules/CancelOrderModal";

export const OrderHistorySection = () => {
  useScrollToTopOnMount();
  const { orders, ordersIsLoading, ordersError, ordersRefetch } =
    useOrdersService();
  const cancellableStatuses = [
    "pending",
    "payment_review",
    "processing",
    "holded",
  ];
  const firstOrder = orders?.[0];
  const isFirstOrderCancellable = firstOrder
    ? cancellableStatuses.includes((firstOrder.Status ?? "").toLowerCase())
    : false;

  const firstOrderId = isFirstOrderCancellable ? firstOrder.MagentoOrderId : 0;

  const [isCancelOrderModalOpen, setIsCancelOrderModalOpen] = useState(false);

  const buttonText = firstOrder?.IsSubscription
    ? "Cancel Subscription"
    : "Request to Cancel";

  const handleOpenModal = () => {
    if (isFirstOrderCancellable) {
      setIsCancelOrderModalOpen(true);
    }
  };

  const closeCancelOrderModal = () => {
    setIsCancelOrderModalOpen(false);
  };
  const capitalize = (text: string | null | undefined) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : "Unknown";
  return (
    <>
      <div className="rounded-[20px] bg-[#f6f7f9] p-8" id="v-pills-tabContent">
        <div
          id={DASHBOARD_TABS.ORDER}
          role="tabpanel"
          aria-labelledby={DASHBOARD_TABS.ORDER}
          className="block opacity-100"
        >
          <Typography
            variant="darkBackgroundHeading"
            as="h3"
            className="flex animate-fade-in-up justify-between"
          >
            Order History
            <Button
              variant="secondary"
              size="md"
              className="hidden md:flex lg:flex"
              aria-label="Cancel Order"
              onClick={handleOpenModal}
              disabled={!isFirstOrderCancellable}
            >
              {buttonText}
            </Button>
          </Typography>
          {ordersIsLoading ? (
            <OrderTableShimmer />
          ) : !ordersIsLoading && !orders?.length ? (
            <div className="w-fit mx-auto px-4">
              <div className="rounded-[20px] min-h-[40vh] lg:min-h-[35vh] flex items-center justify-center">
                <AppEmptyState message="No orders found." />
              </div>
            </div>
          ) : !ordersIsLoading && ordersError ? (
            <div className="w-fit mx-auto px-4">
              <div className="rounded-[20px] min-h-[40vh] lg:min-h-[35vh] flex items-center justify-center">
                <AppErrorState message="Failed to load orders." />
              </div>
            </div>
          ) : orders?.length > 0 && !ordersIsLoading ? (
            <TableGrid
              columns={[...orderColumns]}
              data={orders.map((o) => ({
                id: o.MagentoOrderId,
                product: o.ProductName,
                total: `$${o.TotalPaid.toFixed(2)}`,
                date: o.DatePlaced
                  ? new Date(o.DatePlaced).toLocaleString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })
                  : "—",
                orderStatus: capitalize(o.Status),
              }))}
            />
          ) : (
            <></>
          )}
        </div>
      </div>
      <CancelOrderModal
        isOpen={isCancelOrderModalOpen}
        onClose={closeCancelOrderModal}
        orderId={firstOrderId}
        payloadOrderId={firstOrder?.Id}
        IsSubscription={firstOrder?.IsSubscription || false}
        refetchOrders={ordersRefetch}
      />
    </>
  );
};
export default OrderHistorySection;
